import { generate } from '@pdfme/generator';
import {
  text, multiVariableText, image, svg, table, barcodes,
  line, rectangle, ellipse, dateTime, date, time,
  select, radioGroup, checkbox,
} from '@pdfme/schemas';
import type { Template } from '@pdfme/common';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { mergeBasePdfs } from '../../helpers';
import { font } from '../../utils/fonts';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const plugins = {
  text, multiVariableText, image, svg, table,
  line, rectangle, ellipse, dateTime, date, time,
  select, radioGroup, checkbox,
  ...barcodes,
};

async function rasterizePdf(bytes: Uint8Array, dpi = 300): Promise<Uint8Array> {
  const srcDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
  const outDoc = await PDFDocument.create();

  for (let i = 1; i <= srcDoc.numPages; i++) {
    const page = await srcDoc.getPage(i);
    const scale = dpi / 72;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d')!;

    await page.render({ canvasContext: ctx as unknown as CanvasRenderingContext2D, viewport }).promise;

    const pngBase64 = canvas.toDataURL('image/png').split(',')[1];
    const pngBytes = Uint8Array.from(atob(pngBase64), (c) => c.charCodeAt(0));

    const pngImage = await outDoc.embedPng(pngBytes);
    const pageWidthPts = viewport.width / scale;
    const pageHeightPts = viewport.height / scale;
    const outPage = outDoc.addPage([pageWidthPts, pageHeightPts]);
    outPage.drawImage(pngImage, { x: 0, y: 0, width: pageWidthPts, height: pageHeightPts });
  }

  return outDoc.save();
}

async function validateFonts(): Promise<void> {
  await Promise.all(
    Object.entries(font).map(async ([name, cfg]) => {
      const url = typeof cfg.data === 'string' ? cfg.data : null;
      if (!url) return;
      try {
        const res = await fetch(url);
        const ct = res.headers.get('content-type') ?? '';
        if (!res.ok || ct.includes('html')) {
          console.error(`[Font] "${name}" failed — ${url} → ${res.status} (${ct})`);
        }
      } catch (e) {
        console.error(`[Font] "${name}" fetch error — ${url}`, e);
      }
    })
  );
}

async function runGenerate(template: Template, inputs: Record<string, string>): Promise<Uint8Array> {
  console.log('[PDF] runGenerate — schema pages:', template.schemas?.length, '| basePdf type:', typeof template.basePdf);
  await validateFonts();
  const result = await generate({ template, inputs: [inputs], plugins, options: { font } });
  const rasterized = await rasterizePdf(result);
  console.log('[PDF] runGenerate — output:', rasterized.byteLength, 'bytes (rasterized at 300 DPI)');
  return rasterized;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function generateAndDownloadPdf(
  template: Template,
  inputs: Record<string, string>,
  filename: string
): Promise<Blob> {
  console.log('[PDF] generateAndDownloadPdf —', filename);
  try {
    const pdf = await runGenerate(template, inputs);
    const blob = new Blob([pdf as unknown as BlobPart], { type: 'application/pdf' });
    triggerDownload(blob, filename);
    console.log('[PDF] download triggered —', filename);
    return blob;
  } catch (err) {
    console.error('[PDF] generateAndDownloadPdf failed:', err);
    throw err;
  }
}

export async function generateAndDownloadMergedPdf(
  certTemplate: Template,
  certInputs: Record<string, string>,
  rxTemplate: Template,
  rxInputs: Record<string, string>,
  filename: string
): Promise<Blob> {
  console.log('[PDF] generateAndDownloadMergedPdf —', filename);
  try {
    const [certPdf, rxPdf] = await Promise.all([
      runGenerate(certTemplate, certInputs),
      runGenerate(rxTemplate, rxInputs),
    ]);
    console.log('[PDF] merging PDFs...');
    const merged = await mergeBasePdfs(certPdf, rxPdf);
    const blob = new Blob([new Uint8Array(merged)], { type: 'application/pdf' });
    triggerDownload(blob, filename);
    console.log('[PDF] merged download triggered —', filename);
    return blob;
  } catch (err) {
    console.error('[PDF] generateAndDownloadMergedPdf failed:', err);
    throw err;
  }
}
